import { useState } from 'react';
import { Loader2, Trash2 } from 'lucide-react';
import { listingImageSrc } from './panels/NoticeProductImages';
import { AI_IMG, type GenEntry } from './realImages';
import { TIP_LABEL } from './ReasoningBadge';
import { useLanguage } from '../../contexts/LanguageContext';

// What a generated picture was made from and what was asked of it, shown under it in the
// zoom. Everything here is recorded ON the image rather than read off the page, so it
// still answers for an attempt made three runs ago under settings nobody remembers.
//
// The instruction is the only part that exists nowhere else: the schematic, the context
// and the angle are all still on screen in the table, but the sentence typed into the
// regenerate box closed with the dialog. Images made before it was recorded have none and
// never will — it was not kept, so there is nothing to backfill.
export function GeneratedDetails({
  entry,
  schematic,
  onDelete,
}: {
  entry: GenEntry;
  // The part's manual drawing. Every generation is given it as the FIRST image, so it
  // belongs in the row that says what was sent — its absence there read as though the
  // part had been drawn from the product photos alone.
  schematic?: string;
  // Deleting the attempt — offered where the attempts are MADE and nowhere else. The 3D
  // panel shows this same panel over a photograph it is about to reconstruct from, and a
  // delete there would reach back into the generation history from a page about meshes.
  onDelete?: () => Promise<void>;
}) {
  const { t } = useLanguage();
  const [busy, setBusy] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const ctx = entry.contextIds ?? [];
  const label = TIP_LABEL;
  const remove = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await onDelete?.();
    } finally {
      setBusy(false);
    }
  };
  return (
    // CENTRED, ALL OF IT. The thumbnails and the delete button already were, and the labels
    // above them were not — so a panel whose whole width is one column of facts had its
    // headings against the left edge and its pictures out in the middle, with nothing lining
    // up with anything. It sits under a centred picture and a centred counter; this is the
    // same column, and it reads as one now.
    <div className="flex flex-col items-center gap-3 text-center">
      {/* WHICH picture this is, above what was asked of it. The arrows step through a
          part's attempts now, so the panel has to say which one you have landed on — and
          it is the id to quote, not a position in a list that a delete renumbers. */}
      {entry.id != null && (
        <div className={label}>{AI_IMG} #{entry.id}</div>
      )}
      <div>
        <div className={label}>{t('notice.step3.promptUsed')}</div>
        {entry.feedback ? (
          <p className="mt-1 whitespace-pre-wrap text-sm text-slate-700">
            {entry.feedback}
          </p>
        ) : (
          // Said plainly rather than left blank: "nothing was typed" is the answer for a
          // plain redo, and a blank would read as the panel having failed to load it.
          <p className="mt-1 text-sm italic text-slate-400">
            {t('notice.step3.promptNone')}
          </p>
        )}
      </div>

      <div>
        <div className={label}>{t('notice.step3.context')}</div>
        {(schematic || ctx.length > 0) && (
          <div className="mt-1.5 flex flex-wrap justify-center gap-1.5">
            {/* First, as it is in the call itself. Captioned, because a line drawing among
                photographs is obviously not one of them and the row should say what it is
                rather than leave it to be guessed. */}
            {schematic && (
              <figure className="flex w-12 flex-col items-center gap-0.5">
                <img
                  src={schematic}
                  alt=""
                  className="h-12 w-12 rounded-lg border border-slate-200 bg-white object-contain p-0.5"
                />
                <figcaption className="max-w-12 truncate text-[9px] font-semibold uppercase leading-tight tracking-wide text-blue-700">
                  {t('notice.catalog.schematic')}
                </figcaption>
              </figure>
            )}
            {ctx.map((id) => (
              <img
                key={id}
                src={listingImageSrc(id)}
                alt=""
                className="h-12 w-12 rounded-lg border border-slate-200 bg-white object-contain p-0.5"
              />
            ))}
          </div>
        )}
        {/* About the PRODUCT photos only — the schematic above is not one, and goes on
            every call regardless. Two different facts, not one: an attempt sent none, or
            it is old enough that nobody recorded which. */}
        {!ctx.length && (
          <p className="mt-1 text-sm italic text-slate-400">
            {t(entry.contextIds == null ? 'notice.step3.contextUnknown' : 'notice.step3.contextNone')}
          </p>
        )}
      </div>

      {/* Destructive, so it asks once — and asks in place rather than stacking a second
          dialog over the one already covering the page. */}
      {onDelete && (
      <div>
        {confirming ? (
          <div className="flex items-center gap-2">
            <span className="text-xs text-slate-500">
              {t('notice.step3.deleteConfirm')}
            </span>
            <button
              type="button"
              onClick={() => setConfirming(false)}
              className="rounded-lg border border-slate-300 px-2 py-1 text-xs font-medium text-slate-600 transition-colors hover:bg-slate-100"
            >
              {t('notice.catalog.cancel')}
            </button>
            <button
              type="button"
              onClick={remove}
              disabled={busy}
              className="inline-flex items-center gap-1 rounded-lg bg-rose-600 px-2.5 py-1 text-xs font-semibold text-white transition-colors hover:bg-rose-500 disabled:opacity-50"
            >
              {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
              {t('notice.step3.deleteImage')}
            </button>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => setConfirming(true)}
            className="inline-flex items-center gap-1 rounded-lg border border-slate-300 px-2.5 py-1 text-xs font-medium text-slate-500 transition-colors hover:border-rose-400 hover:text-rose-600"
          >
            <Trash2 className="h-3.5 w-3.5" />
            {t('notice.step3.deleteImage')}
          </button>
        )}
      </div>
      )}
    </div>
  );
}
